import { useNavigate } from '@tanstack/react-router';
import { useCollection } from '_/contexts/CollectionContext';
import { useSourceContext } from '_/contexts/SourceContext';
import { useSourceUrl } from '_/features/sources/hooks/use-source-url';
import useCheckSourceUserAssignment from '_/hooks/useCheckSourceUserAssignment';
import useProjectLockAccess from '_/hooks/useProjectLockAccess';
import { useUpdateSource } from '_/hooks/useSources';
import { Button, Tooltip } from 'antd';
import React, { useCallback } from 'react';

interface RestoreVersionButtonProps {
  versionId: string;
}

const RestoreVersionButton = ({ versionId }: RestoreVersionButtonProps): JSX.Element | null => {
  const history = useNavigate();
  const { selectedSource } = useSourceContext();
  const { selectedCollection } = useCollection();
  const { mutate: updateSource } = useUpdateSource(selectedCollection?.id);
  const redirectLocation = useSourceUrl(selectedSource?.slug);
  const projectAccess = useProjectLockAccess();
  const sourceAssigned = useCheckSourceUserAssignment();
  const access = projectAccess || sourceAssigned;

  const onClick = useCallback(() => {
    if (!selectedSource) return;

    updateSource(
      {
        sourceId: selectedSource.id,
        updates: { extractorVersionId: versionId },
      },
      {
        onSuccess: () => {
          if (redirectLocation) {
            void history({ to: redirectLocation });
          }
        },
      },
    );
  }, [updateSource, selectedSource, versionId, redirectLocation, history]);

  if (!selectedSource) {
    return null;
  }

  return versionId === selectedSource.extractorVersionId ? null : (
    <Tooltip title={access ? 'Restore Version' : 'You do not have permission to change extractor versions'}>
      <Button disabled={!access} type="primary" danger onClick={onClick}>
        Restore
      </Button>
    </Tooltip>
  );
};

export default RestoreVersionButton;
